/**
 * Parameters Documentation
 *
 * How to define, configure, and optimize strategy parameters.
 */
import type { DocPage } from '../types';

export const paramsDocs: DocPage[] = [
  {
    id: 'params/defining',
    title: 'Defining Parameters',
    category: 'params',
    tags: ['parameters', 'define', 'param', 'ctx.param', 'int', 'float', 'string', 'boolean', 'type'],
    version: '2.0',
    related: ['api/lifecycle/define', 'params/optimization', 'params/groups'],
    order: 1,
    description: 'How to declare parameters with ctx.param()',
    content: `# Defining Parameters

Parameters let you control strategy behaviour without editing code. They are declared inside \`define()\` and accessed in \`init()\` and \`onBar()\` via \`ctx.p\`.

## Declaring a Parameter

\`\`\`javascript
function define(ctx) {
  ctx.param('period', {
    type: 'int',
    label: 'EMA Period',
    description: 'Look-back period for the EMA indicator',
    default: 14,
    min: 5,
    max: 200,
    step: 1,
    optimize: true,
    group: 'Indicators',
  });
}
\`\`\`

## Parameter Types

### int

Integer values — best for look-back periods, thresholds, and counts.

\`\`\`javascript
ctx.param('period', {
  type: 'int',
  label: 'Period',
  default: 14,
  min: 5,
  max: 200,
  step: 5,
  optimize: true,
});
\`\`\`

| Option | Type | Required | Description |
|--------|------|----------|-------------|
| \`type\` | \`'int'\` | Yes | Integer parameter |
| \`label\` | \`string\` | No | Display label in the UI |
| \`description\` | \`string\` | No | Tooltip text |
| \`default\` | \`number\` | Yes | Default value |
| \`min\` | \`number\` | No | Minimum allowed value |
| \`max\` | \`number\` | No | Maximum allowed value |
| \`step\` | \`number\` | No | Step size (used during optimization grid) |
| \`optimize\` | \`boolean\` | No | Whether this parameter can be optimized |
| \`group\` | \`string\` | No | Group label for UI organization |

### float

Decimal values — useful for multipliers, thresholds, and percentages.

\`\`\`javascript
ctx.param('riskPercent', {
  type: 'float',
  label: 'Risk %',
  default: 2.0,
  min: 0.5,
  max: 10.0,
  step: 0.5,
  optimize: true,
});
\`\`\`

Same options as \`int\` but accepts decimals.

### string

Enum/select values — ideal for choosing between modes or variants.

\`\`\`javascript
ctx.param('source', {
  type: 'string',
  label: 'Price Source',
  default: 'close',
  options: ['open', 'high', 'low', 'close', 'hl2', 'hlc3'],
});
\`\`\`

| Option | Type | Required | Description |
|--------|------|----------|-------------|
| \`type\` | \`'string'\` | Yes | String parameter |
| \`default\` | \`string\` | Yes | Default selected value |
| \`options\` | \`string[]\` | No | Allowed values (displayed as a dropdown) |

### boolean

Toggle values — for enabling/disabling features.

\`\`\`javascript
ctx.param('useTrailingStop', {
  type: 'boolean',
  label: 'Trailing Stop',
  description: 'Enable ATR-based trailing stop',
  default: false,
});
\`\`\`

## Accessing Parameters

Parameters are available in \`init()\` and \`onBar()\` via \`ctx.p\`:

\`\`\`javascript
function init(ctx) {
  ctx.indicator('ema', 'EMA', { period: ctx.p.period, source: 'close' });
}

function onBar(ctx, i) {
  const threshold = ctx.p.riskPercent / 100;
  if (ctx.p.useTrailingStop) {
    // trailing stop logic
  }
}
\`\`\`

> Parameters are read-only at runtime. All values are set before the backtest begins.

## Validation Rules

- Every parameter **must** have a \`type\` and a \`default\` value
- \`type\` must be one of: \`'int'\`, \`'float'\`, \`'string'\`, \`'boolean'\`
- If \`min\` is provided but \`default\` is not, the engine will use \`min\` as the fallback
- Duplicate parameter names will overwrite silently — use unique names
`,
  },
  {
    id: 'params/optimization',
    title: 'Optimization Settings',
    category: 'params',
    tags: ['optimization', 'optimize', 'grid', 'range', 'min', 'max', 'step', 'tuning'],
    version: '2.0',
    related: ['params/defining', 'runs/optimization', 'runs/wfa'],
    order: 2,
    description: 'How parameters are used during optimization and WFA',
    content: `# Optimization Settings

Parameters with \`optimize: true\` can be automatically tuned by the optimizer. The engine explores different combinations of parameter values across a grid to find the best-performing configuration.

## Marking a Parameter for Optimization

\`\`\`javascript
ctx.param('fastLength', {
  type: 'int',
  label: 'Fast EMA',
  default: 9,
  min: 5,
  max: 50,
  step: 5,
  optimize: true,   // <-- this makes it tunable
});
\`\`\`

When \`optimize: true\`, the optimizer uses \`min\`, \`max\`, and \`step\` to build a search grid:

| Setting | Purpose |
|---------|---------|
| \`min\` | Lowest value to test |
| \`max\` | Highest value to test |
| \`step\` | Increment between tested values |

For the example above, the optimizer tests: **5, 10, 15, 20, 25, 30, 35, 40, 45, 50**.

## Grid Size Warning

The total number of combinations = product of each parameter's step count. Large grids take longer:

| Parameters | Steps Each | Total Combinations |
|-----------|-----------|-------------------|
| 2 | 10 | 100 |
| 3 | 10 | 1,000 |
| 4 | 10 | 10,000 |
| 3 | 20 | 8,000 |

> Keep the grid manageable. Prefer fewer parameters with wider steps for initial discovery, then narrow the ranges.

## Fixed vs Optimized

Parameters without \`optimize: true\` (or with \`optimize: false\`) keep their \`default\` value throughout the optimization run. This is useful for structural settings that should not change:

\`\`\`javascript
// Fixed — always 'close'
ctx.param('source', {
  type: 'string',
  default: 'close',
  options: ['open', 'high', 'low', 'close'],
});

// Optimized — engine tests 5 through 50
ctx.param('period', {
  type: 'int',
  default: 14,
  min: 5,
  max: 50,
  step: 5,
  optimize: true,
});
\`\`\`

## Step Size Tips

| Parameter | Recommended Step | Reasoning |
|-----------|-----------------|-----------|
| EMA/SMA period (5–200) | 5–10 | Periods below 5 are noisy |
| RSI threshold (20–80) | 5 | Standard increments |
| ATR multiplier (1.0–5.0) | 0.5 | Meaningful granularity |
| Risk percentage (0.5–5.0) | 0.5 | Small change = big impact |

## Interaction with Walk-Forward Analysis

During WFA, the optimizer runs independently on each training window. Parameters are re-optimized per window and validated on the out-of-sample segment. This tests whether the parameter surface is stable over time.

See [Walk-Forward Analysis](runs/wfa) for details.
`,
  },
  {
    id: 'params/groups',
    title: 'Parameter Groups',
    category: 'params',
    tags: ['groups', 'organization', 'ui', 'display', 'group'],
    version: '2.0',
    related: ['params/defining', 'params/optimization'],
    order: 3,
    description: 'Organizing parameters into groups for the UI',
    content: `# Parameter Groups

Use the \`group\` option to organize parameters into sections in the sidebar panel. This has no effect on execution — it is purely for display.

## Usage

\`\`\`javascript
function define(ctx) {
  // Indicator group
  ctx.param('fastLength', {
    type: 'int', label: 'Fast EMA', default: 9,
    min: 5, max: 50, step: 5, optimize: true,
    group: 'Indicators',
  });
  ctx.param('slowLength', {
    type: 'int', label: 'Slow EMA', default: 21,
    min: 10, max: 200, step: 10, optimize: true,
    group: 'Indicators',
  });

  // Risk group
  ctx.param('stopMultiplier', {
    type: 'float', label: 'Stop ATR Mult', default: 2.0,
    min: 1.0, max: 5.0, step: 0.5, optimize: true,
    group: 'Risk Management',
  });
  ctx.param('useTrailingStop', {
    type: 'boolean', label: 'Trailing Stop', default: false,
    group: 'Risk Management',
  });
}
\`\`\`

## Result in the UI

The Parameters panel groups them under collapsible headers:

- **Indicators** — Fast EMA, Slow EMA
- **Risk Management** — Stop ATR Mult, Trailing Stop

## Tips

- Parameters without a \`group\` appear under a default "General" section
- Keep group names short — they display in a sidebar panel
- Consistent naming across strategies helps when switching between them
`,
  },
];
